/**
 * Purchasing › Follow-ups (TASK-0247, INC-3): the words and links for each kind of follow-up. Pure, so
 * the table and its tests share them.
 */
import type { FollowUp, FollowUpKind } from '@/lib/api/types';
import { money, qty } from '@/lib/format';
import { lateOrderActions, type OrderActionSet } from '../close-short';

export const FOLLOW_UP_KIND_ORDER: FollowUpKind[] = [
  'over_receipt',
  'implausible',
  'not_ordered',
  'counted_less_than_invoice',
  'cost_pending',
  'overdue_order',
  'invoices_waiting',
  'billed_not_received',
  'received_not_billed',
  'short_still_coming',
  'possible_double_receipt',
];

/** Short names for the filter chips and the badge on each row. */
export const FOLLOW_UP_LABEL: Record<FollowUpKind, string> = {
  over_receipt: 'Over the order',
  implausible: 'Check the count',
  not_ordered: 'Not ordered',
  counted_less_than_invoice: 'Counted less than the invoice',
  cost_pending: 'No cost yet',
  overdue_order: 'Late order',
  invoices_waiting: 'Invoice waiting',
  billed_not_received: 'Credit to chase',
  received_not_billed: 'Received, not billed',
  short_still_coming: 'Short, still coming',
  possible_double_receipt: 'Possible double receipt',
};

export const FOLLOW_UP_TONE: Record<FollowUpKind, 'warn' | 'info' | 'negative'> = {
  over_receipt: 'warn',
  implausible: 'negative',
  not_ordered: 'info',
  counted_less_than_invoice: 'warn',
  cost_pending: 'info',
  overdue_order: 'warn',
  invoices_waiting: 'info',
  billed_not_received: 'warn',
  received_not_billed: 'info',
  short_still_coming: 'warn',
  possible_double_receipt: 'negative',
};

/** One sentence: what happened and what to look at. */
export function followUpSentence(row: FollowUp): string {
  const ordered = row.orderedQty ?? 0;
  const received = row.receivedQty ?? 0;
  switch (row.kind) {
    case 'over_receipt':
      return `Received ${qty(received)} of ${qty(ordered)} ordered: ${qty(received - ordered)} over.`;
    case 'implausible':
      return `Received ${qty(received)} on ${qty(ordered)} ordered: check the count was typed right.`;
    case 'not_ordered':
      return `Received ${qty(received)} that was not on the order.`;
    case 'counted_less_than_invoice':
      return 'The floor counted less than the invoice checked in. Check the delivery and the bill.';
    case 'cost_pending':
      return `Received ${qty(received)} with no cost yet: enter the invoice cost.`;
    case 'overdue_order':
      return `${row.ageDays === 1 ? '1 day' : `${row.ageDays} days`} past its expected day and not all in.`;
    case 'invoices_waiting':
      return 'A scanned invoice is waiting to be recorded on this order.';
    case 'billed_not_received':
      return `Billed on invoice ${row.invoiceNumber ?? '(no number)'} but did not come${
        row.amountCents != null ? `: ${money(row.amountCents)} to get credited` : ''
      }.`;
    case 'received_not_billed':
      return `Counted at the door ${row.ageDays} days ago and no invoice yet.`;
    case 'short_still_coming':
      return `Still coming: ${qty(Math.max(0, ordered - received))} of ${qty(ordered)} not in yet.`;
    case 'possible_double_receipt':
      return `Entered as a different delivery while PO #${row.relatedPoId} for the same items is still open. Check it is not the same goods twice.`;
  }
}

/**
 * Where a row's "Open" goes: the receive screen, the order's invoices, or its lines. Review N1: an
 * over-receipt, an implausible count or an item not ordered usually leaves the order received, where
 * the Receive tab is not offered, so those open the lines (received against ordered) instead of
 * silently falling back to them. A short still coming is on an open order: it opens Receive.
 */
export function followUpTarget(row: FollowUp): {
  mode: 'receive' | 'invoices' | 'lines' | 'costs';
  invoice?: number;
} {
  switch (row.kind) {
    // TASK-0252 (INC-5): a line with no cost yet opens the order's Invoice costs.
    case 'cost_pending':
      return { mode: 'costs' };
    case 'invoices_waiting':
    case 'billed_not_received':
    case 'received_not_billed':
    case 'counted_less_than_invoice':
      return row.invoiceId != null
        ? { mode: 'invoices', invoice: row.invoiceId }
        : { mode: 'invoices' };
    case 'short_still_coming':
      return { mode: 'receive' };
    default:
      return { mode: 'lines' };
  }
}

/**
 * TASK-0251 (INC-4): the actions a late order or a short still coming gets on its row: Receive, Close
 * short once something came, Cancel only for a late order nothing came on. Other kinds get none.
 */
export function followUpActions(row: FollowUp): OrderActionSet | null {
  if (row.kind !== 'overdue_order' && row.kind !== 'short_still_coming') return null;
  if (!row.poStatus) return null;
  if (row.kind === 'overdue_order') {
    // A late order's row carries what was received on the whole order.
    return lateOrderActions({ status: row.poStatus, receivedQty: row.receivedQty });
  }
  // A short still coming is one line of an order an invoice was recorded on: never cancelled from here.
  return {
    ...lateOrderActions({ status: row.poStatus, receivedQty: null }),
    cancel: false,
  };
}
